"use client";

import {
  CalendarDays,
  Clock,
  MapPin,
  Building2,
} from "lucide-react";
import { formatConsistentDate } from "@/utils/timezone-date-utils";
import { quoteThemeCssVars } from "../quote-theme/quote-theme-css-vars";
import { iExcursionLegDetails } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { ImageGallery } from "@/components/common/image-gallery/image-gallery";
import { HtmlContentWithReadMore } from "@/components/common/html-content-with-read-more";

export interface ExcursionItineraryInnerProps extends iExcursionLegDetails {
  primaryColor?: string;
  day?: number;
}

export function ExcursionItineraryInner(props: ExcursionItineraryInnerProps) {
  const primaryColor = quoteThemeCssVars.primary;
  const lightColor = quoteThemeCssVars.primary10;

  const formatDate = (date: string | null) => {
    if (!date) return "";
    return formatConsistentDate(date, "EEE, d MMM yyyy");
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Excursion Header */}
      {props.excursionName && (
        <div className="mb-4 sm:mb-6">
          <h3 className="text-lg sm:text-xl font-semibold">{props.excursionName}</h3>
        </div>
      )}

      {/* Overview/Body */}
      {props.body && (
        <HtmlContentWithReadMore
          html={props.body}
          className="mb-4 sm:mb-6"
          maxWords={40}
        />
      )}

      {/* Gallery */}
      {props.gallery && props.gallery.length > 0 && (
        <div className="mb-4 sm:mb-6">
          <ImageGallery images={props.gallery} />
        </div>
      )}

      {/* Details */}
      <div className="space-y-4 sm:space-y-6">
        {props.date && (
          <div className="flex items-start gap-2 sm:gap-3">
            <CalendarDays
              className="w-4 h-4 sm:w-5 sm:h-5 mt-1"
              style={{ color: primaryColor }}
            />
            <div>
              <div className="font-medium text-sm sm:text-base">
                <span className="hidden sm:inline">Date</span>
                <span className="sm:hidden">Date</span>
              </div>
              <div className="text-sm sm:text-base">{formatDate(props.date)}</div>
              {props.time && (
                <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
                  <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Start time: </span>
                  <span className="sm:hidden">Time: </span>
                  {props.time}
                </div>
              )}
              {props.arrivalNotes && (
                <div className="text-sm text-gray-600 mt-2">{props.arrivalNotes}</div>
              )}
            </div>
          </div>
        )}

        {props.location && (
          <div className="flex items-start gap-2 sm:gap-3">
            <MapPin
              className="w-4 h-4 sm:w-5 sm:h-5 mt-1"
              style={{ color: primaryColor }}
            />
            <div>
              <div className="font-medium text-sm sm:text-base">
                <span className="hidden sm:inline">Meeting Point</span>
                <span className="sm:hidden">Meeting</span>
              </div>
              <div className="text-sm text-gray-600">{props.location}</div>
            </div>
          </div>
        )}

        {props.duration && (
          <div className="flex items-start gap-2 sm:gap-3">
            <Clock className="w-4 h-4 sm:w-5 sm:h-5 mt-1" style={{ color: primaryColor }} />
            <div>
              <div className="font-medium text-sm sm:text-base">
                <span className="hidden sm:inline">Duration</span>
                <span className="sm:hidden">Duration</span>
              </div>
              <div className="text-sm text-gray-600">{props.duration}</div>
            </div>
          </div>
        )}

        {props.provider && (
          <div className="flex items-start gap-2 sm:gap-3">
            <Building2
              className="w-4 h-4 sm:w-5 sm:h-5 mt-1"
              style={{ color: primaryColor }}
            />
            <div>
              <div className="font-medium text-sm sm:text-base">
                <span className="hidden sm:inline">Provider</span>
                <span className="sm:hidden">Provider</span>
              </div>
              <div className="text-sm text-gray-600">{props.provider}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
